"use client";

import { useEffect, useState } from "react";
import { updateWorkingHours, type Settings } from "@/lib/settings-api";

interface Props {
  settings: Settings | null;
  onUpdate: (s: Settings) => void;
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);

function formatHourLabel(hour: number): string {
  if (hour === 0) return "12 AM (midnight)";
  if (hour === 12) return "12 PM (noon)";
  if (hour === 24) return "12 AM next day";
  if (hour < 12) return `${hour} AM`;
  return `${hour - 12} PM`;
}

// User-settable working-hours window. Whatever the user picks here
// becomes the boundary for AI Time Box / schedule generation —
// blocks are never placed before day_start_hour or past day_end_hour
// in their local clock. The setting falls back to 8 AM–8 PM when
// missing so existing accounts keep their legacy behavior until
// they touch this control.
export default function WorkingHoursSection({ settings, onUpdate }: Props) {
  const [start, setStart] = useState<number>(settings?.day_start_hour ?? 8);
  const [end, setEnd] = useState<number>(settings?.day_end_hour ?? 20);
  const [hasLocalEdits, setHasLocalEdits] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const settingsReady = Boolean(settings);

  useEffect(() => {
    if (hasLocalEdits) return;
    setStart(settings?.day_start_hour ?? 8);
    setEnd(settings?.day_end_hour ?? 20);
  }, [hasLocalEdits, settings?.day_start_hour, settings?.day_end_hour]);

  const isInverted = end <= start;
  const canSave = settingsReady && !saving && !isInverted;

  const handleSave = async () => {
    if (!canSave) return;
    setSaveError(null);
    setSaving(true);
    try {
      const updated = await updateWorkingHours(start, end);
      setHasLocalEdits(false);
      onUpdate(updated);
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : "Could not save working hours yet.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="bg-card rounded-2xl border border-border shadow-sm p-4"
      data-testid="settings-working-hours-section"
    >
      <h2 className="text-sm font-semibold text-text-light mb-1">Working hours</h2>
      <p className="text-[11px] leading-relaxed text-muted mb-3">
        AI Time Box only places blocks inside this window. If you&apos;re a
        night owl, push the end later. The label on the AI plan still
        respects what &quot;morning&quot;, &quot;lunch&quot;, and &quot;afternoon&quot; mean for you.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <select
          data-testid="settings-working-hours-start"
          disabled={!settingsReady}
          value={start}
          onChange={(e) => {
            setHasLocalEdits(true);
            setStart(Number(e.target.value));
          }}
          className="flex-1 min-w-[140px] px-3 py-2 rounded-lg border border-border bg-card text-xs text-text focus:outline-none focus:ring-2 focus:ring-forest/30"
        >
          {HOUR_OPTIONS.map((hour) => (
            <option key={`start-${hour}`} value={hour}>
              {formatHourLabel(hour)}
            </option>
          ))}
        </select>
        <span className="text-xs text-muted">to</span>
        <select
          data-testid="settings-working-hours-end"
          disabled={!settingsReady}
          value={end}
          onChange={(e) => {
            setHasLocalEdits(true);
            setEnd(Number(e.target.value));
          }}
          className="flex-1 min-w-[140px] px-3 py-2 rounded-lg border border-border bg-card text-xs text-text focus:outline-none focus:ring-2 focus:ring-forest/30"
        >
          {HOUR_OPTIONS.map((hour) => (
            <option key={`end-${hour}`} value={hour}>
              {formatHourLabel(hour)}
            </option>
          ))}
          <option value={24}>{formatHourLabel(24)}</option>
        </select>
        <button
          type="button"
          data-testid="settings-working-hours-save"
          disabled={!canSave}
          onClick={handleSave}
          className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
            canSave
              ? "text-forest bg-forest/10 hover:bg-forest/20"
              : "cursor-not-allowed bg-secondary text-muted"
          }`}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>

      {isInverted && (
        <p className="mt-2 text-xs text-earth">
          End must be later than start.
        </p>
      )}
      {saveError && <p className="mt-2 text-xs text-earth">{saveError}</p>}
    </div>
  );
}
